import { API } from "@/enums/costanti";
import { useFetchJSON } from "@/db/dbManag";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// Interfaccia per i valori delle opzioni
// Adatta questa struttura in base alla risposta effettiva dell'API
export interface OptionsValues {
  [category: string]: Array<{
    value: string;
    label: string;
    [key: string]: unknown;
  }>;
}

export interface OptionsState {
  values: OptionsValues;
  isLoading: boolean;
  error: string | null;
  lastFetched: Date | null;
}

@Module({
  namespaced: true,
})
export default class OptionsModule extends VuexModule implements OptionsState {
  values: OptionsValues = {};
  isLoading = false;
  error: string | null = null;
  lastFetched: Date | null = null;

  /**
   * Ottieni tutti i valori delle opzioni
   */
  get optionsValues(): OptionsValues {
    return this.values;
  }

  /**
   * Ottieni i valori per una specifica categoria
   * @param category - Nome della categoria
   */
  get getOptionsByCategory() {
    return (category: string) => {
      return this.values[category] || [];
    };
  }

  /**
   * Verifica se le opzioni sono in caricamento
   */
  get isOptionsLoading(): boolean {
    return this.isLoading;
  }

  /**
   * Ottieni l'errore se presente
   */
  get optionsError(): string | null {
    return this.error;
  }

  /**
   * Verifica se i dati sono stati già caricati
   */
  get areOptionsLoaded(): boolean {
    return this.lastFetched !== null && Object.keys(this.values).length > 0;
  }

  @Mutation
  [Mutations.SET_OPTIONS_VALUES](payload: OptionsValues): void {
    this.values = payload;
    this.lastFetched = new Date();
    this.error = null;
  }

  @Mutation
  [Mutations.SET_OPTIONS_LOADING](payload: boolean): void {
    this.isLoading = payload;
  }

  @Mutation
  [Mutations.SET_OPTIONS_ERROR](payload: string | null): void {
    this.error = payload;
    this.isLoading = false;
  }

  @Action({ rawError: true })
  async [Actions.FETCH_OPTIONS_VALUES](): Promise<void> {
    // Evita chiamate multiple se già in caricamento
    if (this.isLoading) {
      return;
    }

    this.context.commit(Mutations.SET_OPTIONS_LOADING, true);
    this.context.commit(Mutations.SET_OPTIONS_ERROR, null);

    try {
      const url = API.GET.OPTIONS_VALUES;
      const { data, execute } = useFetchJSON(url).get().json();

      await execute();

      if (data.value) {
        // Assumendo che l'API restituisca un oggetto con le opzioni
        // Adatta questa logica in base alla struttura effettiva della risposta
        console.log("Dati opzioni ricevuti dall'API options:", data.value);
        const optionsData = data.value.success ? data.value.data : data.value;
        this.context.commit(Mutations.SET_OPTIONS_VALUES, optionsData);
      } else {
        throw new Error("Nessun dato ricevuto dall'API");
      }
    } catch (error: unknown) {
      console.error("Errore nel caricamento delle opzioni:", error);
      const errorMessage =
        (error as Error).message ||
        "Errore durante il caricamento delle opzioni";
      this.context.commit(Mutations.SET_OPTIONS_ERROR, errorMessage);
    } finally {
      this.context.commit(Mutations.SET_OPTIONS_LOADING, false);
    }
  }

  @Action({ rawError: true })
  async fetchOptionsIfNeeded(): Promise<void> {
    // Carica le opzioni solo se non sono già state caricate
    if (!this.areOptionsLoaded) {
      await this.context.dispatch(Actions.FETCH_OPTIONS_VALUES);
    }
  }

  @Action({ rawError: true })
  async refreshOptions(): Promise<void> {
    // Forza il ricaricamento delle opzioni
    await this.context.dispatch(Actions.FETCH_OPTIONS_VALUES);
  }
}
